import React, { useRef, useMemo } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Typography, Box, TableFooter } from "@mui/material";

// Helper function to format numbers as currency/decimal
const formatNumber = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Checks if a column header represents a numeric value that should be right-aligned
 * and formatted.
 */
const isNumericHeader = (header) => {
    const upperHeader = header.toUpperCase();
    
    // Explicitly exclude descriptive keys
    if (upperHeader.includes('CODE') || 
        upperHeader.includes('NAME') || 
        upperHeader.includes('ID') || 
        upperHeader.includes('MAN') || 
        upperHeader.includes('CUSTOMER') ||
        upperHeader.includes('ACCOUNT') ||
        upperHeader.includes('GROUP') ||
        upperHeader.includes('DEPTNO') ||
        upperHeader.includes('DATE') 
    ) {
        return false;
    }
    
    // Check if it contains common numeric keywords
    // return ['SALES', 'RETURN', 'VAT', 'COMMISSION', 'NET',  'AMT', 'COST', 'PRICE', 'QTY', 'AMOUNT', 'PROFIT', 'GROSS SALE','DISC','NET SALES'].some(s => upperHeader.includes(s));

    // make stricter check instead of some because vattype is string value but since vat is in array it will consider vattype as numeric so nan
    return ['SALES', 'RETURN', 'VAT', 'COMMISSION', 'NET',  'AMT', 'COST', 'PRICE', 'QTY', 'AMOUNT', 'PROFIT', 'GROSS SALE','DISC','NET SALES'].includes(
        header.replace(/\s+/g, '_').toUpperCase()
      );

};

/**
 * Reusable component to render any data array as a scrollable table with a grand total footer.
 * @param {Array<Object>} rows - The data array.
 * @param {Object} loading - Loading state.
 * @param {Function} loadNextPage - Function to load more data.
 * @param {Boolean} hasMore - Indicator if more data is available.
 * @param {Array<String>} footerTotalKeys - Array of column keys whose values should be summed in the footer.
 * @param {String} ariaLabel - Accessibility label for the table.
 */
const DynamicTableForVatReport = ({
    rows,
    loading,
    loadNextPage,
    hasMore,
    stickyColumns = [], 
    footerTotalKeys = [],
    ariaLabel = "data report"
}) => {
    
    const tableContainerRef = useRef(null);
    
    const handleTableScroll = () => {
        const currentTarget = tableContainerRef.current;
        if (!currentTarget || loading || !hasMore || (rows && rows.length === 0)) return;

        const { scrollTop, scrollHeight, clientHeight } = currentTarget;

        if (scrollHeight - scrollTop <= clientHeight + 10) {
            loadNextPage();
        }
    };

    // 1. Determine all display columns 
    const allDisplayKeys = useMemo(() => {
        if (!rows || rows.length === 0) return [];
        
        const allKeys = Object.keys(rows[0]);
        
        // Filter out internal control keys
        const filteredKeys = allKeys.filter(key =>
            !['isGroupHeader', 'isSubHeader', 'isSubSubHeader', 'totalAmount', 'totalAmountExclVAT', 'YEAR'].includes(key)
        );
        
        const remainingKeys = filteredKeys.filter(key => !stickyColumns.includes(key));
        
        return [...stickyColumns, ...remainingKeys];
    }, [rows, stickyColumns]);
    
    // 2. Calculate the Grand Totals internally ONLY for the FOOTER_TOTAL_KEYS
    const internalGrandTotals = useMemo(() => {
        if (!rows || rows.length === 0 || footerTotalKeys.length === 0) {
            return {};
        }

        const totals = {};

        // Calculate the total sum for the specified numeric columns
        for (const key of footerTotalKeys) {
            totals[key] = rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
        }

        return totals;
    }, [rows, footerTotalKeys]);
    
    // Keys that will actually display a total, in the correct display order
    const totalValueKeys = allDisplayKeys.filter(key => footerTotalKeys.includes(key));
    const totalColumns = allDisplayKeys.length;
    
    // Calculate the column span for the "GRAND TOTAL:" label
    const descriptiveSpan = useMemo(() => {
        if (totalValueKeys.length === 0 || allDisplayKeys.length === 0) return totalColumns; 

        // Find the index of the first column that displays a total
        const firstTotalKey = totalValueKeys[0];
        const firstTotalIndex = allDisplayKeys.indexOf(firstTotalKey);
        
        // The span covers all columns from the start up to (but not including) the first total column.
        return firstTotalIndex !== -1 ? firstTotalIndex : totalColumns;
    }, [allDisplayKeys, totalColumns, totalValueKeys]);


    // Return null if no data and not loading (safe guard against undefined rows)
    if (!rows || (rows.length === 0 && !loading)) return null;


    const renderHeader = () => {
        if (allDisplayKeys.length === 0) return null; 

        return (
            <TableHead>
                <TableRow>
                    {allDisplayKeys.map((columnKey) => {
                        return (
                            <TableCell 
                                key={columnKey} 
                                align={isNumericHeader(columnKey) ? "right" : "left"}
                                sx={{ minWidth: 120, whiteSpace: 'nowrap', fontWeight: 'bold' }} 
                            >
                                {columnKey.replace(/([A-Z])/g, ' $1').trim().toUpperCase()}
                            </TableCell>
                        );
                    })}
                </TableRow>
            </TableHead>
        );
    };

    const renderBody = () => {
        return (
            <TableBody>
                {rows.map((row, index) => {
                    
                    return (
                        <TableRow key={`dynamic-row-${index}`} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                            
                            {allDisplayKeys.map((columnKey) => {
                                return (
                                    <TableCell 
                                        key={columnKey}
                                        align={isNumericHeader(columnKey) ? "right" : "left"}
                                    >
                                        {/* Render formatted number if numeric, otherwise render raw value */}
                                        {isNumericHeader(columnKey)
                                            ? formatNumber(row[columnKey]) 
                                            : (row[columnKey] === null || row[columnKey] === undefined ? 'N/A' : row[columnKey])
                                        }
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    );
                })}

                
                {/* Load More Indicator */}
                {hasMore && (
                    <TableRow>
                        <TableCell colSpan={totalColumns} align="center">
                            <Box sx={{ py: 2 }}>
                                <CircularProgress size={24} />
                                <Typography variant="caption" display="block">
                                    Loading more data...
                                </Typography>
                            </Box>
                        </TableCell>
                    </TableRow>
                )}
                

                {!loading && rows.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={totalColumns} align="center">
                            <Typography variant="subtitle1" color="textSecondary">No data found.</Typography>
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        );
    };


    const renderFooterRow = () => {
        // Only render footer if we have columns to total and the calculations produced values
        if (Object.keys(internalGrandTotals).length === 0) {
            return null; 
        }

        // --- STICKY FOOTER STYLES ---
        const stickyCellStyle = { 
            fontWeight: 'bold', 
            fontSize: '1.1em', 
            padding: '12px 16px', 
            backgroundColor: '#e0f2f1', 
            borderTop: '3px double #004d40',
            // CRITICAL: Makes the footer cells sticky at the bottom of the TableContainer
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
        };

        return (
            <TableRow>
                
                {/* 1. Descriptive Span for the GRAND TOTAL label */}
                {/* If descriptiveSpan is 0, this cell renders as the first data cell */}
                <TableCell 
                    colSpan={descriptiveSpan || 1} 
                    align="right"
                    sx={{ 
                        ...stickyCellStyle,
                        borderRight: descriptiveSpan > 0 ? '1px solid #004d40' : 'none',
                    }}>
                    {descriptiveSpan > 0 ? 'GRAND TOTAL:' : 'TOTAL:'}
                </TableCell>
                
                {/* 2. Data cells for the totals */}
                {allDisplayKeys.slice(descriptiveSpan).map(key => {
                    const isTotalKey = footerTotalKeys.includes(key);
                    return (
                        <TableCell 
                            key={key} 
                            align="right"
                            sx={stickyCellStyle}>
                            {isTotalKey ? formatNumber(internalGrandTotals[key]) : ''}
                        </TableCell>
                    );
                })}
            </TableRow>
        );
    };

    return (
        <Box sx={{ width: '100%', border: '1px solid #ccc', borderRadius: '8px', overflow: 'hidden' }}>
            <TableContainer
                ref={tableContainerRef}
                onScroll={handleTableScroll}
                sx={{
                    // IMPORTANT: The maxHeight must be set here for vertical scrolling to work.
                    // Adjusted for space used by header, filters, and footer margin.
                    maxHeight: 'calc(100vh - 400px)', 
                    overflow: 'auto', // Handles both X and Y scrolling
                    width: '100%',
                }}
            >
                {/* Main data table, must contain Header, Body, and Footer */}
                <Table 
                    stickyHeader 
                    aria-label={ariaLabel} 
                    // Ensures the footer columns align with header/body columns
                    sx={{ tableLayout: 'auto' }} 
                >
                    {renderHeader()}
                    {renderBody()}
                    
                    {/* 🚀 FIX: The TableFooter must be inside the main Table, 
                        which is inside the scrollable TableContainer. */}
                    {Object.keys(internalGrandTotals).length > 0 && (
                        <TableFooter> 
                            {renderFooterRow()}
                        </TableFooter>
                    )}
                </Table>
            </TableContainer>
            
            {/* Global Loading Overlay */}
            {loading && (
                <div style={{ 
                    display: "flex", 
                    justifyContent: 'center', 
                    alignItems: "center", 
                    width: "100%", 
                    height: "100%", 
                    position: "absolute", 
                    zIndex: 100, 
                    backgroundColor: "#ffffffaa", 
                    top: "0", 
                    left: "0",
                    borderRadius: '8px'
                }}>
                    <CircularProgress size={48} />
                </div>
            )}
        </Box>
    );
};

export default DynamicTableForVatReport;